import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  #controller = new AbortController();
  #mainArtwork = null;
  #mainArtworkAlt = '';
  #currentVariantId = null;
  #loadedVariantIds = new Set(); // Track which variants have mocks loaded
  #productHandle = null;
  #userHasSelectedOptions = false;
  #variantChangeInProgress = false; // Prevents selection handler from racing with variant handler
  #pendingSelection = null; // Queued selection to process after variant change completes
  #additionalSlidesAbort = null; // AbortController for in-flight additional-slides fetches
  #variantChangeSeq = 0; // Generation counter — stale variant-change runs stop writing once a newer one starts
  #fullMocksInFlight = new Map(); // variant key → in-flight #loadFullMocks promise (dedups concurrent GraphQL fetches)
  #sizeChangedAt = 0; // Timestamp (ms) of last size-impact:changed — used to keep room mock visible on follow-on frame auto-correction events
  #lastNavigationAt = 0; // Timestamp of last user-driven nav so auto-correction events (e.g. sku-picker swapping frame after a size change) don't bounce the gallery back to slide 0 (abeb)
  #lazyPromoted = false; // Tracks first user interaction with the gallery — once true, all lazy images are flipped to eager (35c7: slides 3+ took 10s+ to load on swipe)

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = () => {
    // Gallery DOM is managed by JS mock components (frame-rendering,
    // room-background-mock, etc). Skip section-rendering replaceWith
    // to avoid clobbering the live mock state on variant change.
  };

  /**
   * Index of the room-mock slide (the configurator's "size in room" view).
   * Resolved by data-slide-role because the Moka lifestyle hero, when
   * present, shifts every positional index by one. Falls back to 1 (the
   * pre-hero position) if the role isn't found.
   */
  #roomMockSlideIndex() {
    const slides = Array.from(this.querySelectorAll('slideshow-slide'));
    const idx = slides.findIndex((s) => s.dataset.slideRole === 'room-mock');
    return idx >= 0 ? idx : 1;
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Handles SKU picker variant changes and loads new gallery data.
   *
   * @param {CustomEvent} event - The sku-picker:variant-changed event.
   */
  #handleSkuPickerVariantChange = async (event) => {
    const variantId = event.detail.variantId;

    // Generation gate: rapid variant flips run this handler concurrently
    // (only #handleSkuPickerUpdate checks #variantChangeInProgress). The
    // fetch is aborted via #additionalSlidesAbort, but the older run's
    // #ensureFullMocksLoaded / #updateGalleryMocks continuations could
    // still write stale mocks after the newer run finished. Each await
    // below re-checks the sequence and bails if superseded.
    const seq = ++this.#variantChangeSeq;
    const isStale = () => seq !== this.#variantChangeSeq;

    // Prevent selection handler from racing with this handler
    this.#variantChangeInProgress = true;

    // Kick off the additional-slides refresh in parallel — this swaps the
    // lifestyle / auxiliary slides (after the frame + room mocks) for ones
    // sourced from the new variant's configurator metaobject. Mocks update
    // via their own events, so we never replace slides 0/1.
    const additionalSlidesPromise = this.#refreshAdditionalSlides(variantId);

    try {
      // Update current variant
      this.#currentVariantId = variantId;

      // Hook: allow subclasses to prepare for variant change (e.g., show loading overlay)
      this._beforeVariantChange(variantId);

      // Ensure full mocks are loaded for this variant
      // This will only fetch if not already loaded
      await this.#ensureFullMocksLoaded();
      if (isStale()) return; // a newer variant change owns the gallery now

      // Load gallery data for new variant
      const galleryData = this.#loadGalleryData(variantId);
      if (!galleryData) return;

      // Hook: wait for any subclass dependencies (e.g., photo crop completion)
      await this._waitForVariantDependencies();
      if (isStale()) return;

      // Reset gallery to default mocks for this variant
      // (no option selections yet, so use first/best match)
      const matchingMocks = this.#findMatchingMocks(galleryData, {});
      await this.#updateGalleryMocks(matchingMocks);

      // Wait for the additional-slides refresh to settle so any rebinding
      // (config buttons etc.) sees the final DOM. Errors are already logged
      // inside the helper, so we just swallow here.
      await additionalSlidesPromise.catch(() => {});
    } catch (error) {
      console.error('Error handling variant change:', error);
    } finally {
      // Only the latest run cleans up — a superseded run returning early
      // must not clear the in-progress flag or steal the queued selection
      // while the newer run is still working.
      if (isStale()) return;
      this.#variantChangeInProgress = false;

      // Process any selection that was queued during variant change
      if (this.#pendingSelection) {
        const { selection, userInitiated } = this.#pendingSelection;
        this.#pendingSelection = null;
        console.log('[MediaGallery] Processing queued selection after variant change');

        if (userInitiated) {
          this.#userHasSelectedOptions = true;
        }
        // Process asynchronously to avoid blocking the finally block
        const sizeChangedAt = this.#sizeChangedAt;
        this.#processSelection(selection).then(() => {
          // Match #handleSkuPickerUpdate: navigate to the configurator slide
          // showing the change unless this is a recent follow-on event (frame
          // auto-correction). Navigates from any slide, including lifestyle (abeb).
          if (userInitiated) {
            const now = Date.now();
            const isFollowOn = (now - this.#lastNavigationAt) < 1200;
            if (!isFollowOn && (now - sizeChangedAt) < 500) {
              // Size change → show the lifestyle/room mock. Frame changes no
              // longer pull back to the plain product mock — the room mock
              // re-renders the new frame in place (QA thread).
              // Resolved by role, not index: the Moka hero slide shifts every
              // index by one when present (10/07).
              this.slideshow?.select(this.#roomMockSlideIndex(), undefined, { animate: true });
              this.#lastNavigationAt = now;
            }
          }
        });
      }

      // Hook: allow subclasses to clean up after variant change
      this._afterVariantChange();
    }
  };

  /**
   * Refreshes the gallery's "additional" slides (everything after the
   * frame + room mocks — lifestyle, auxiliary product media, video) by
   * fetching the section via the Section Rendering API for the new
   * variant. Frame mock, room mock, and overlay nodes (wishlist pill,
   * view-similar pill, title chip) are preserved.
   *
   * No-ops when the variant is already current (e.g. style-picker emitted
   * a sync event for the variant the page already loaded with).
   *
   * @param {string} variantId - The new variant ID.
   * @returns {Promise<void>}
   */
  async #refreshAdditionalSlides(variantId) {
    if (!variantId) return;
    // No-op if the variant hasn't actually changed since last refresh.
    if (variantId === this.#currentVariantId) return;

    // Resolve section id from the wrapping shopify-section. Guard against
    // dialogs / nested mounts where this ancestor might not exist.
    const sectionWrapper = this.closest('.shopify-section');
    if (!sectionWrapper) return;
    const sectionId = sectionWrapper.id?.replace(/^shopify-section-/, '');
    if (!sectionId) return;

    // Abort any in-flight fetch from a previous rapid click.
    this.#additionalSlidesAbort?.abort();
    this.#additionalSlidesAbort = new AbortController();
    const { signal } = this.#additionalSlidesAbort;

    // Build product URL — strip any existing query so we can append our own.
    const productPath = window.location.pathname;
    const url = `${productPath}?variant=${encodeURIComponent(variantId)}&section_id=${encodeURIComponent(sectionId)}`;

    let html;
    try {
      const response = await fetch(url, { signal });
      if (!response.ok) {
        console.warn('[MediaGallery] Additional-slides fetch returned', response.status);
        return;
      }
      html = await response.text();
    } catch (err) {
      if (err?.name === 'AbortError') return; // superseded by a newer fetch
      console.warn('[MediaGallery] Additional-slides fetch failed', err);
      return;
    }

    if (signal.aborted) return;

    // Parse the response and locate the matching gallery in it.
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const newGallery = parsed.querySelector(this.tagName.toLowerCase());
    if (!newGallery) return;

    // Pull out the lists of "additional" slides + thumbnails from both old
    // and new galleries. We replace strictly the additional-role nodes;
    // frame / room / overlay nodes stay in place.
    const oldSlideshow = this.querySelector('slideshow-component');
    const newSlideshow = newGallery.querySelector('slideshow-component');
    if (!oldSlideshow || !newSlideshow) return;

    this.#patchAdditionalSlides(oldSlideshow, newSlideshow);

    // Each .slideshow-controls block (desktop + mobile thumbnail strip) needs
    // its additional thumbnails swapped to match the new slide set.
    const oldControls = this.querySelectorAll('slideshow-controls');
    const newControls = newGallery.querySelectorAll('slideshow-controls');
    oldControls.forEach((oldEl, idx) => {
      const newEl = newControls[idx];
      if (newEl) this.#patchAdditionalThumbnails(oldEl, newEl);
    });

    // Update the inline --slideshow-timeline scope so the new slide count
    // is reflected by view-timeline scrolling. The Liquid renders this on
    // <slideshow-component style="..."> — copy the value forward.
    const newStyle = newSlideshow.getAttribute('style');
    if (newStyle) oldSlideshow.setAttribute('style', newStyle);

    // If the previously-selected slide index no longer exists, snap back to
    // the frame mock (slide 0). Calling .select() also re-syncs the
    // active thumbnail aria-selected state.
    const slideshow = this.slideshow;
    if (slideshow) {
      const slideCount = oldSlideshow.querySelectorAll('slideshow-slide[ref="slides[]"]').length;
      if (typeof slideshow.current === 'number' && slideshow.current >= slideCount) {
        try { slideshow.select(0, undefined, { animate: false }); } catch (_e) { /* best-effort */ }
      }
    }

    // Re-bind handlers on any new "Choose This Configuration" buttons that
    // landed in the patched slides.
    this.#initializeConfigurationButtons();
  }

  /**
   * Replaces the additional-role slides in `oldSlideshow` with the
   * additional-role slides from `newSlideshow`, preserving frame/room/video
   * slides and any non-slide children.
   *
   * @param {Element} oldSlideshow
   * @param {Element} newSlideshow
   */
  #patchAdditionalSlides(oldSlideshow, newSlideshow) {
    const oldScroller = oldSlideshow.querySelector('slideshow-slides');
    const newScroller = newSlideshow.querySelector('slideshow-slides');
    if (!oldScroller || !newScroller) return;

    const oldAdditional = Array.from(
      oldScroller.querySelectorAll(':scope > slideshow-slide[data-slide-role="additional"], :scope > slideshow-slide[data-slide-role="video"]')
    );
    const newAdditional = Array.from(
      newScroller.querySelectorAll(':scope > slideshow-slide[data-slide-role="additional"], :scope > slideshow-slide[data-slide-role="video"]')
    );

    if (oldAdditional.length === 0 && newAdditional.length === 0) return;

    // Find the anchor — the last preserved slide (frame mock, room mock,
    // anything that isn't "additional"/"video"). New slides are inserted
    // immediately after it; if there is no preserved slide we just append.
    const preservedSlides = Array.from(
      oldScroller.querySelectorAll(':scope > slideshow-slide[ref="slides[]"]')
    ).filter(el => !oldAdditional.includes(el));
    const anchor = preservedSlides[preservedSlides.length - 1] || null;

    // Remove old additional slides.
    for (const slide of oldAdditional) slide.remove();

    // Update the view-timeline-name index on each new additional slide so
    // it matches its final position. Then insert.
    const insertAfter = (parent, node, after) => {
      if (after && after.nextSibling) parent.insertBefore(node, after.nextSibling);
      else parent.appendChild(node);
    };

    let cursor = anchor;
    const baseIndex = preservedSlides.length;
    newAdditional.forEach((slide, i) => {
      const finalIndex = baseIndex + i;
      const style = slide.getAttribute('style') || '';
      const rewritten = style.replace(/view-timeline-name:\s*--slide-\d+/, `view-timeline-name: --slide-${finalIndex}`);
      slide.setAttribute('style', rewritten);
      slide.setAttribute('aria-hidden', 'true');
      insertAfter(oldScroller, slide, cursor);
      cursor = slide;
    });

    // 35c7: if the user has already engaged with the gallery, any lazy
    // images that came in via the variant section render need to be
    // promoted to eager too, otherwise the next swipe stalls again.
    if (this.#lazyPromoted) {
      oldScroller.querySelectorAll('img[loading="lazy"]').forEach(img => {
        img.loading = 'eager';
        img.fetchPriority = 'high';
      });
    }
  }

  /**
   * Replaces additional-role thumbnails in a slideshow-controls element so
   * the thumbnail strip stays in sync with the patched slide list.
   *
   * @param {Element} oldEl
   * @param {Element} newEl
   */
  #patchAdditionalThumbnails(oldEl, newEl) {
    const oldStrip = oldEl.querySelector('.slideshow-controls__thumbnails');
    const newStrip = newEl.querySelector('.slideshow-controls__thumbnails');
    if (!oldStrip || !newStrip) return;

    const oldAdditional = Array.from(
      oldStrip.querySelectorAll(':scope > [data-slide-role="additional"], :scope > [data-slide-role="video"]')
    );
    const newAdditional = Array.from(
      newStrip.querySelectorAll(':scope > [data-slide-role="additional"], :scope > [data-slide-role="video"]')
    );

    if (oldAdditional.length === 0 && newAdditional.length === 0) return;

    const allOldButtons = Array.from(oldStrip.querySelectorAll(':scope > .slideshow-control'));
    const preserved = allOldButtons.filter(el => !oldAdditional.includes(el));
    const anchor = preserved[preserved.length - 1] || null;

    for (const btn of oldAdditional) btn.remove();

    // Re-number the on:click="/select/<i>" attributes so they map to the
    // new slide positions.
    let cursor = anchor;
    const baseIndex = preserved.length;
    newAdditional.forEach((btn, i) => {
      const finalIndex = baseIndex + i;
      btn.setAttribute('on:click', `/select/${finalIndex}`);
      btn.removeAttribute('aria-selected');
      if (cursor && cursor.nextSibling) oldStrip.insertBefore(btn, cursor.nextSibling);
      else oldStrip.appendChild(btn);
      cursor = btn;
    });
  }

  // ============================================
  // Protected hook methods for subclass override
  // ============================================

  /**
   * Hook called before variant change processing begins.
   * Override in subclasses to show loading states or prepare for change.
   * @param {string} variantId - The new variant ID
   */
  _beforeVariantChange(variantId) {
    // Base implementation does nothing - override in subclasses
  }

  /**
   * Hook to wait for dependencies before updating gallery.
   * Override in subclasses to wait for async operations (e.g., photo cropping).
   * @returns {Promise<void>}
   */
  async _waitForVariantDependencies() {
    // Base implementation does nothing - override in subclasses
  }

  /**
   * Hook called after variant change completes.
   * Override in subclasses to hide loading states or perform cleanup.
   */
  _afterVariantChange() {
    // Base implementation does nothing - override in subclasses
  }

  /**
   * Returns the uploaded photo URL if one exists.
   * Override in subclasses that support photo uploads.
   * @returns {string|null} The uploaded photo URL or null.
   */
  _getUploadedPhotoUrl() {
    return null;
  }

  /**
   * Hook to control whether gallery mocks should be updated via JavaScript.
   * Override in subclasses to return false if mock content should be preserved
   * (e.g., AI Photos before/after comparison that's rendered in Liquid).
   * @returns {boolean} True if mocks should be updated, false to skip updates.
   */
  _shouldUpdateGalleryMocks() {
    return true;
  }

  /**
   * Updates gallery mocks with uploaded photo or reverts to original artwork.
   * Override in subclasses that support photo uploads.
   * @returns {Promise<void>}
   */
  async _updateGalleryWithUploadedPhoto() {
    // Base implementation does nothing - override in subclasses
  }

  /**
   * Initializes interactive buttons in the gallery (configuration and view similar).
   * This method extracts the button initialization logic that was previously in inline scripts.
   * It needs to be called both on initial page load and after gallery replacements.
   */
  #initializeConfigurationButtons() {
    // Initialize "Choose This Configuration" buttons
    const configButtons = this.querySelectorAll('.choose-config-btn[data-mock-options]');

    configButtons.forEach(btn => {
      // Skip if already initialized (avoid duplicate listeners without expensive cloneNode)
      if (btn.dataset.initialized) return;
      btn.dataset.initialized = 'true';

      // Prevent slideshow from handling clicks on this button
      btn.setAttribute('on:click', 'none');

      // Add click handler
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        try {
          const optionsJson = btn.dataset.mockOptions;
          const options = JSON.parse(optionsJson);

          if (!options || options.length === 0) {
            console.warn('[Choose Config] No options found for this mock');
            return;
          }

          console.log('[Choose Config] Setting configuration from mock');

          // Find the SKU picker instance
          const skuPickerContainer = document.getElementById('sku-picker-container');
          if (!skuPickerContainer) {
            console.error('[Choose Config] SKU picker container not found');
            return;
          }

          // Access the SkuPicker instance
          const skuPicker = window.skuPickerInstance;
          if (!skuPicker) {
            console.error('[Choose Config] SKU picker instance not found');
            return;
          }

          // Group options by type and get handles
          // Process in TWO passes to ensure we set all values before validation
          const newState = { ...skuPicker.state.confirmed };

          // First pass: collect all the options from the mock
          const mockOptions = {
            size: null,
            framing: null,
            border: null,
            frame_color: null
          };

          options.forEach(option => {
            if (option.type === 'size') {
              mockOptions.size = option.handle;
            } else if (option.type === 'framing') {
              mockOptions.framing = option.handle;
            } else if (option.type === 'border') {
              mockOptions.border = option.handle;
            } else if (option.type === 'frame_color') {
              mockOptions.frame_color = option.handle;
            }
          });

          // Second pass: apply all options together
          if (mockOptions.size) {
            newState.size_option = mockOptions.size;
          }

          if (mockOptions.framing) {
            newState.framing_option = mockOptions.framing;
            // Set frame color from mock if provided, otherwise use first color
            if (mockOptions.frame_color) {
              newState.frame_color = mockOptions.frame_color;
            } else {
              const frameData = skuPicker.data.options.framing?.find(f => f.handle === mockOptions.framing);
              if (frameData && frameData.colors && frameData.colors.length > 0) {
                newState.frame_color = frameData.colors[0].handle;
              }
            }
          }

          if (mockOptions.border) {
            newState.border_option = mockOptions.border;
          }

          // Validate: if we have mount but no frame, default to a frame
          if (newState.border_option === 'mount' && newState.framing_option === 'unframed') {
            // Find first available frame for this size
            const firstAvailableFrame = skuPicker.data.options.framing?.find(f => {
              return f.handle !== 'unframed' && skuPicker.components.framing[newState.size_option]?.[f.handle];
            });

            if (firstAvailableFrame) {
              newState.framing_option = firstAvailableFrame.handle;
              if (firstAvailableFrame.colors && firstAvailableFrame.colors.length > 0) {
                newState.frame_color = firstAvailableFrame.colors[0].handle;
              }
              console.log('[Choose Config] Mount requires frame - auto-selected:', firstAvailableFrame.handle);
            }
          }

          // Update the confirmed state
          skuPicker.state.confirmed = newState;

          // Call the update method to refresh UI and form
          skuPicker._updateConfirmedSelection(true);
          skuPicker._renderOptionRows();

          // Scroll to SKU picker
          setTimeout(() => {
            skuPickerContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }, 100);

        } catch (err) {
          console.error('[Choose Config] Error setting configuration:', err);
        }
      }, { capture: true });
    });

    // Initialize "View Similar" button
    const viewSimilarBtn = this.querySelector('.view-similar--v2');
    if (viewSimilarBtn && !viewSimilarBtn.dataset.initialized) {
      viewSimilarBtn.dataset.initialized = 'true';

      // Prevent slideshow from handling clicks on this button
      viewSimilarBtn.setAttribute('on:click', 'none');

      // Add click handler
      viewSimilarBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        // Scroll to the products grid (has scroll-margin-top for header + sticky pills)
        const target = document.querySelector('.tags-discovery-grid__products');
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, { capture: true });
    }

    // Wire up preset-based button labels (async — waits for style-picker readiness)
    this.#initConfigButtonLabels();
  }

  /**
   * Wires up the product title chip (mobile overlay) to scroll to and open
   * the "About this art" accordion. Also hides the chip on the last slide.
   * Idempotent — skips if already initialized.
   */
  #initializeTitleChip() {
    const chip = this.querySelector('.pdp-title-chip');
    if (!chip || chip.dataset.chipInit) return;
    chip.dataset.chipInit = 'true';

    // Click → scroll to "About this art" accordion and open it
    chip.addEventListener('click', () => {
      const accordion = document.querySelector('accordion-custom[data-about-art]');
      if (!accordion) return;
      const details = accordion.querySelector('details');
      if (details && !details.open) details.open = true;
      accordion.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });

    // Hide chip on non-first slides
    this.addEventListener('slideshow:select', (e) => {
      chip.hidden = e.detail.index !== 0;
    }, { signal: this.#controller.signal });
  }

  /**
   * Matches a mock's option handles against resolved style presets.
   * Returns the first matching preset, or null if none match.
   */
  #matchPresetToMockOptions(mockOptions, presets) {
    const framing = mockOptions.find(o => o.type === 'framing')?.handle;
    const frameColor = mockOptions.find(o => o.type === 'frame_color')?.handle || null;
    const border = mockOptions.find(o => o.type === 'border')?.handle || 'no-border';

    return presets.find(p =>
      p.resolvedFraming === framing &&
      p.frameColorHandle === frameColor &&
      p.border === border
    ) || null;
  }

  /**
   * Updates all config button labels with preset name, size bucket, and dimensions.
   * Called when presets become available and when size changes.
   */
  #updateConfigButtonLabels() {
    const stylePicker = document.querySelector('style-picker');
    const presets = stylePicker?.resolvedPresets;
    if (!presets?.length) return;

    const configButtons = this.querySelectorAll('.choose-config-btn[data-mock-options]');
    configButtons.forEach(btn => {
      try {
        const mockOptions = JSON.parse(btn.dataset.mockOptions);
        if (!mockOptions?.length) return;

        const preset = this.#matchPresetToMockOptions(mockOptions, presets);
        if (!preset) return; // Keep generic text

        const sizeLabel = this.#computeMockSizeLabel(mockOptions);
        const span = btn.querySelector('span');
        if (span) {
          span.textContent = sizeLabel
            ? `${preset.name} ${sizeLabel}`
            : preset.name;
        }
      } catch { /* keep generic text on parse error */ }
    });
  }

  static #SIZE_BUCKETS = [
    { maxInches: 12, label: 'Nook' },
    { maxInches: 16, label: 'Accent' },
    { maxInches: 32, label: 'Focal Point' },
    { maxInches: Infinity, label: 'Statement' },
  ];

  /**
   * Returns a formatted size label like "Statement (30 × 40")" for the mock's size.
   */
  #computeMockSizeLabel(mockOptions) {
    const skuPicker = window.skuPickerInstance;
    if (!skuPicker?.data?.options?.size) return null;

    const mockSize = mockOptions.find(o => o.type === 'size')?.handle;
    const sizeHandle = mockSize || skuPicker.state.confirmed.size_option;
    const sizeOption = skuPicker.data.options.size.find(s => s.handle === sizeHandle);
    if (!sizeOption) return null;

    const w = Math.round(parseFloat(sizeOption.width_inches));
    const h = Math.round(parseFloat(sizeOption.height_inches));
    if (!w || !h) return null;

    const maxDim = Math.max(w, h);
    const bucket = MediaGallery.#SIZE_BUCKETS.find(b => maxDim <= b.maxInches)
      || MediaGallery.#SIZE_BUCKETS[MediaGallery.#SIZE_BUCKETS.length - 1];

    const unit = skuPicker.currentUnit || 'in';
    if (unit === 'cm') {
      const wCm = parseFloat(sizeOption.width_cms);
      const hCm = parseFloat(sizeOption.height_cms);
      if (wCm && hCm) return `${bucket.label} (${wCm} × ${hCm}cm)`;
    }

    return `${bucket.label} (${w} × ${h}")`;
  }

  /**
   * Waits for style-picker readiness, then updates button labels.
   * Also listens for size changes to recompute prices.
   */
  #initConfigButtonLabels() {
    const { signal } = this.#controller;

    // If style-picker is already ready, update immediately
    const stylePicker = document.querySelector('style-picker');
    if (stylePicker?.resolvedPresets?.length) {
      this.#updateConfigButtonLabels();
    }

    // Listen for style-picker:ready (fires after presets are resolved)
    document.addEventListener('style-picker:ready', () => {
      this.#updateConfigButtonLabels();
    }, { signal });

    // Re-compute prices when size changes (price depends on selected size)
    document.addEventListener('size-impact:changed', () => {
      // Small delay so sku-picker state updates first
      requestAnimationFrame(() => this.#updateConfigButtonLabels());
    }, { signal });

    // Re-compute labels when unit preference changes (cm ↔ in)
    document.addEventListener('sku-picker:unit-changed', () => {
      this.#updateConfigButtonLabels();
    }, { signal });
  }

  /**
   * Handles SKU picker modal opened - preloads mocks to avoid render delay.
   *
   * @param {CustomEvent} event - The sku-picker:modal-opened event.
   */
  #handleSkuPickerModalOpened = async (event) => {
    // Preload full mocks when modal opens, so they're ready when user selects
    await this.#ensureFullMocksLoaded();
  };

  /**
   * Handles SKU picker selection changes and updates gallery mocks.
   *
   * @param {CustomEvent} event - The sku-picker:selection-changed event.
   */
  #handleSkuPickerUpdate = async (event) => {
    const selection = event.detail.selection;
    const userInitiated = event.detail.userInitiated;

    // If a variant change is in progress, queue the selection to process after it completes
    // This prevents race conditions while ensuring user selections aren't lost
    if (this.#variantChangeInProgress) {
      if (userInitiated) {
        console.log('[MediaGallery] Variant change in progress, queueing selection for later');
        this.#pendingSelection = { selection, userInitiated };
      }
      return;
    }

    // Only update mocks if user has actively selected an option
    // This prevents updating on page load with default values
    if (!userInitiated && !this.#userHasSelectedOptions) {
      return;
    }

    // Mark that user has made a selection
    if (userInitiated) {
      this.#userHasSelectedOptions = true;
    }

    await this.#processSelection(selection);

    // Navigate to the most relevant slide:
    //   - size change → room mock (slide 1)
    //   - frame change → frame mock (slide 0)
    //
    // Suppress the second nav after a recent user-initiated nav (1.2s
    // window). This stops the sku-picker's follow-on "I auto-corrected
    // the frame because the old one isn't available at this size"
    // selection-changed event from bouncing the gallery back to slide 0
    // after a size change at sizes like 39×52 / 42×56 (abeb).
    //
    // Navigate regardless of the current slide: if the user is on a
    // lifestyle/detail slide (2+) when they change style or size, pull them
    // back to the configurator slide so the change is visible.
    if (userInitiated) {
      const now = Date.now();
      const isFollowOn = (now - this.#lastNavigationAt) < 1200;
      if (!isFollowOn && (now - this.#sizeChangedAt) < 500) {
        // Size change → lifestyle/room mock; frame changes stay on the
        // current slide (the room mock re-renders in place — QA thread).
        // Resolved by role, not index (Moka hero shifts indices — 10/07).
        this.slideshow?.select(this.#roomMockSlideIndex(), undefined, { animate: true });
        this.#lastNavigationAt = now;
      }
    }
  };

  /**
   * Processes a selection and updates gallery mocks.
   * Extracted to allow reuse from both direct handler and queued selection processing.
   *
   * @param {Object} selection - The selection object with size_option, framing_option, border_option, frame_color.
   */
  async #processSelection(selection) {
    try {
      // Mocks should already be loaded from modal-opened event
      // But ensure they're loaded as fallback
      await this.#ensureFullMocksLoaded();

      const galleryData = this.#loadGalleryData(this.#currentVariantId);
      if (!galleryData) {
        console.warn('[MediaGallery] No gallery data found for variant:', this.#currentVariantId);
        return;
      }

      const matchingMocks = this.#findMatchingMocks(galleryData, selection);

      await this.#updateGalleryMocks(matchingMocks);
    } catch (error) {
      console.error('[MediaGallery] Error updating mocks:', error);
    }
  }

  connectedCallback() {
    super.connectedCallback();

    // disconnectedCallback aborts the controller; listeners added with an
    // already-aborted signal are silent no-ops, so a reconnected element
    // (view transition, morph move) would never hear another event. Make a
    // fresh controller for each connected lifecycle.
    if (this.#controller.signal.aborted) {
      this.#controller = new AbortController();
    }

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    // Listen for SKU picker modal opening (preload mocks to avoid render delay)
    document.addEventListener('sku-picker:modal-opened', this.#handleSkuPickerModalOpened, { signal });

    // Record timestamp when size changes so #handleSkuPickerUpdate navigates to room mock.
    // Capture phase ensures this fires before sku-picker's target-phase handler fires selection-changed,
    // and the timestamp window (500ms) absorbs follow-on selection-changed events caused by the
    // sku-picker auto-correcting the frame style to one available at the new size.
    document.addEventListener('size-impact:changed', () => { this.#sizeChangedAt = Date.now(); }, { signal, capture: true });

    // Listen for SKU picker selection changes
    document.addEventListener('sku-picker:selection-changed', this.#handleSkuPickerUpdate, { signal });

    // Listen for SKU picker variant changes
    document.addEventListener('sku-picker:variant-changed', this.#handleSkuPickerVariantChange, { signal });

    // Listen for config changes (e.g. size slider) and forward to frame mocks
    document.addEventListener('gallery-wall:config-changed', this.#handleConfigChange, { signal });

    // Sync room mock thumbnail when the main slide's room background loads or changes
    document.addEventListener('room-background:ready', (e) => {
      this.#updateRoomThumbnailBackground(e.detail.roomBg, e.detail.source);
    }, { signal });

    // Re-initialize after section morph (inline scripts don't execute via DOMParser)
    document.addEventListener('section:morph', () => {
      this.#initializeConfigurationButtons();
      this.#initializeTitleChip();
    }, { signal });

    // Detect and load initial variant
    this.#detectAndLoadInitialVariant();

    // Defer initialization — connectedCallback fires before
    // the parser has finished the element's children in some cases.
    requestAnimationFrame(() => {
      this.#initializeConfigurationButtons();
      this.#initializeTitleChip();
    });

    // On first interaction (touch / pointer / arrow nav), promote every
    // lazy slide image to eager + high fetch priority so the next swipe
    // doesn't wait on a fresh CDN fetch. Previously slides 3+ could
    // sit blank for 10s+ on mobile because the browser only requests
    // a lazy image once the slide enters the viewport (35c7).
    const slideshow = this.querySelector('slideshow-component');
    if (slideshow) {
      const promote = () => {
        if (this.#lazyPromoted) return;
        this.#lazyPromoted = true;
        slideshow.querySelectorAll('img[loading="lazy"]').forEach(img => {
          img.loading = 'eager';
          img.fetchPriority = 'high';
        });
      };
      slideshow.addEventListener('pointerdown', promote, { signal, passive: true });
      slideshow.addEventListener('touchstart', promote, { signal, passive: true });
      slideshow.addEventListener('keydown', promote, { signal });
    }
  }

  /**
   * Detects the initial variant and loads its gallery data
   */
  async #detectAndLoadInitialVariant() {
    // Don't load full mocks on page load - they'll be loaded on first SKU picker interaction
    // This improves initial page load performance

    // Try URL parameter first
    const urlParams = new URLSearchParams(window.location.search);
    const variantParam = urlParams.get('variant');
    if (variantParam) {
      this.#currentVariantId = variantParam;
      // Note: Don't update mocks here - default mocks are already shown
      // Full mocks will load when user interacts with SKU picker
      return;
    }

    // Try product form
    const productForm = document.querySelector('form[action*="/cart/add"]');
    if (productForm) {
      const variantInput = productForm.querySelector('[name="id"]');
      if (variantInput && variantInput.value) {
        this.#currentVariantId = variantInput.value;
        // Note: Don't update mocks here - default mocks are already shown
        // Full mocks will load when user interacts with SKU picker
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#additionalSlidesAbort?.abort();
  }

  /**
   * Called by the morph library after DOM diffing updates this element's
   * children in-place (without disconnect/reconnect). Re-initializes click
   * handlers on any new "Choose This Configuration" buttons that were
   * inserted by the morph.
   */
  updatedCallback() {
    this.#initializeConfigurationButtons();
    this.#initializeTitleChip();
  }

  /**
   * Forwards gallery-wall:config-changed events to child frame and room mocks.
   */
  #handleConfigChange = (event) => {
    const detail = event.detail || {};
    const config = {};

    if (detail.artworkWidth !== undefined) config.artworkWidth = detail.artworkWidth;
    if (detail.frameColorHex) config.frameColor = detail.frameColorHex;
    if (detail.mouldingTile !== undefined) config.mouldingTile = detail.mouldingTile;
    if (detail.mouldingWidth !== undefined) config.mouldingWidth = detail.mouldingWidth;
    if (detail.hasFrame !== undefined) config.hasFrame = detail.hasFrame;
    if (detail.borderOption !== undefined) config.borderOption = detail.borderOption;
    if (detail.borderWidth !== undefined) config.borderWidth = detail.borderWidth;
    if (detail.productType !== undefined) config.productType = detail.productType;

    if (Object.keys(config).length === 0) return;

    // Keep existing canvas loop — canvas still used by in-room slide / other templates.
    this.querySelectorAll('gallery-wall-frame-mock').forEach(el => {
      if (el.updateConfig) el.updateConfig(config);
    });

    // CSS hero swap: update the SSR mock-image hero when framing changes.
    // Unframed / wrapped-canvas are NOT special-cased — the configurator has
    // real mock-images for them (print/canvas shown at the framed dimensions).
    // Use getProductMockForPreset (same resolver as the frame cards): it matches
    // on the 'unframed' option, whereas _getSelectedMockHandle matches on the
    // stale frame_color and wrongly returns a framed mock for an unframed pick.
    this.ensureFullMocksLoaded().then(() => {
      const c = window.skuPickerInstance?.state?.confirmed;
      if (!c) return; // no selection — leave SSR default in place
      const mock = this.getProductMockForPreset({
        resolvedVariantId: this.#currentVariantId,
        resolvedFraming:   c.framing_option,
        frameColorHandle:  c.framing_option === 'unframed' ? null : c.frame_color,
        border:            c.border_option || 'no-border',
      });
      if (!mock || !mock.frameImage) return; // matching failed — keep current
      this.#swapHeroMock(mock);
    }).catch(() => {});
  };

  /**
   * Swaps the CSS mock-image slides to match the given mock object (shape:
   * { frameImage, placementTop, placementLeft, placementWidth, transform }).
   *
   * Updates BOTH the hero frame slide and the frame-mock thumbnail(s) so the
   * thumbnail mirrors the hero exactly. Both carry data-slide-role="frame-mock"
   * (set in _product-media-gallery.liquid for the hero and slideshow-controls.liquid
   * for the thumbnail) and render via snippets/mock-image.liquid, so the same
   * frame image + placement geometry applies to each. Falls back to the first
   * .mock-image-container if the role attribute is absent.
   *
   * @param {Object} mock - The mock data object.
   */
  #swapHeroMock(mock) {
    let containers = Array.from(this.querySelectorAll('[data-slide-role="frame-mock"] .mock-image-container'));
    if (!containers.length) {
      const first = this.querySelector('.mock-image-container');
      containers = first ? [first] : [];
    }
    for (const container of containers) {
      this.#applyMockToContainer(container, mock);
    }
  }

  /**
   * Applies a mock's frame image and artwork placement to a single CSS
   * mock-image container (hero slide or frame thumbnail).
   *
   * @param {HTMLElement} container - A .mock-image-container element.
   * @param {Object} mock - The mock data object.
   */
  #applyMockToContainer(container, mock) {
    // In is_main (LCP) mode, mock-image.liquid renders the frame as an <img
    // class="mock-image__frame-bg"> for eager/fetchpriority loading.
    // In non-main mode it uses background-image on .mock-image__frame-container.
    // (Unframed/canvas use real mocks — frameImage is the print/canvas overlay.)
    const frameImg = container.querySelector('.mock-image__frame-bg');
    const frameBg = container.querySelector('.mock-image__frame-container');
    const placement = container.querySelector('.mock-image__placement');

    if (frameImg) {
      frameImg.style.visibility = '';
      // Skip the swap when it points at the same underlying file: SSR renders
      // width=800&quality=60 while materialized mocks store width=1600, so a
      // naive src assignment refetches the identical frame and replaces the
      // eagerly-loaded LCP image with a late-discovered one.
      if (!MediaGallery.#sameMockFile(frameImg.src, mock.frameImage)) {
        // A real swap (different file) must drop the SSR srcset or the
        // browser keeps rendering the old candidate. Safari needs the
        // reflow between removal and src write (same fix as #updateMediaElement).
        frameImg.removeAttribute('srcset');
        void frameImg.offsetHeight;
        frameImg.src = mock.frameImage;
      }
    } else if (frameBg) {
      // Extract the URL from background-image: url(...) for the same-file check.
      const existingBg = frameBg.style.backgroundImage.replace(/^url\(["']?|["']?\)$/g, '');
      if (!MediaGallery.#sameMockFile(existingBg, mock.frameImage)) {
        frameBg.style.backgroundImage = `url(${mock.frameImage})`;
      }
    }

    // Update artwork placement geometry (percentages — resolution-independent,
    // so the same values apply to the hero and the smaller thumbnail).
    if (placement) {
      placement.style.height = '';
      placement.style.top = `${mock.placementTop}%`;
      placement.style.left = `${mock.placementLeft}%`;
      placement.style.width = `${mock.placementWidth}%`;
      placement.style.transform = mock.transform || 'none';
    }
  }

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }

  /**
   * Loads and parses the GalleryData JSON for a specific variant.
   *
   * @param {string} variantId - The variant ID to load data for.
   * @returns {Object|null} - Parsed gallery data object or null if not found.
   */
  #loadGalleryData(variantId) {
    const dataElement = document.getElementById('GalleryData');
    if (!dataElement) {
      console.warn('GalleryData script element not found');
      return null;
    }

    try {
      const allData = JSON.parse(dataElement.textContent);

      // Check if data is variant-based (new structure) or legacy
      let data;
      if (allData.variants && variantId) {
        // New variant-based structure
        data = allData.variants[variantId];
        if (!data) {
          console.warn(`No gallery data found for variant ${variantId}`);
          return null;
        }
      } else if (allData.mainArtwork) {
        // Legacy structure (backwards compatibility)
        data = allData;
      } else {
        console.error('Unrecognized GalleryData structure');
        return null;
      }

      // Store main artwork reference for later use
      if (data.mainArtwork) {
        this.#mainArtwork = data.mainArtwork;
        this.#mainArtworkAlt = data.mainArtworkAlt || '';
      }

      return data;
    } catch (error) {
      console.error('Failed to parse GalleryData:', error);
      return null;
    }
  }

  /**
   * Ensures full mocks data is loaded when needed.
   * Lazy-loads from the product-gallery-mocks section if not already loaded.
   *
   * @returns {Promise<boolean>} - True if full mocks are available, false otherwise.
   */
  async #ensureFullMocksLoaded() {
    // Check if mocks for current variant are already loaded. When currentVariantId
    // is still null (before variant detection), a prior call fetched ALL variants
    // (extractConfiguratorGids with null) — so a non-empty loaded set means we're
    // done; without this, every pre-detection call re-fetched.
    if (
      (this.#currentVariantId && this.#loadedVariantIds.has(this.#currentVariantId)) ||
      (!this.#currentVariantId && this.#loadedVariantIds.size > 0)
    ) {
      return true;
    }

    // In-flight dedup: concurrent callers (modal-open preload + a variant
    // change, say) both pass the loaded check above before either finishes,
    // and would each fire the same GraphQL fetch. Share one promise per
    // variant key instead.
    const inFlightKey = this.#currentVariantId || '*';
    const inFlight = this.#fullMocksInFlight.get(inFlightKey);
    if (inFlight) return inFlight;
    const load = this.#loadFullMocks()
      .finally(() => this.#fullMocksInFlight.delete(inFlightKey));
    this.#fullMocksInFlight.set(inFlightKey, load);
    return load;
  }

  /**
   * Performs the actual GraphQL fetch + merge for #ensureFullMocksLoaded.
   * Call through #ensureFullMocksLoaded, which adds caching + in-flight dedup.
   *
   * @returns {Promise<boolean>} - True if full mocks are available.
   */
  async #loadFullMocks() {
    // Get the data element
    const dataElement = document.getElementById('GalleryData');
    if (!dataElement) {
      console.warn('[MediaGallery] GalleryData element not found');
      return false;
    }

    // Get product handle from page
    if (!this.#productHandle) {
      this.#productHandle = this.#getProductHandle();
      if (!this.#productHandle) {
        console.warn('[MediaGallery] Could not determine product handle for lazy-loading mocks');
        return false;
      }
    }

    try {
      // Get configurator GIDs from current gallery data
      const currentData = JSON.parse(dataElement.textContent);

      // Only fetch mocks for the current variant to improve performance
      // If no current variant, this will fetch all variants as fallback
      const configuratorGids = this.#extractConfiguratorGids(currentData, this.#currentVariantId);

      if (configuratorGids.length === 0) {
        console.warn('[MediaGallery] No configurator GIDs found in gallery data');
        return false;
      }

      // Fetch configurator data via GraphQL Storefront API (only for current variant)
      const fullMocksData = await this.#fetchMocksViaGraphQL(configuratorGids);

      if (!fullMocksData) {
        console.warn('[MediaGallery] Failed to fetch mocks via GraphQL');
        return false;
      }

      // Re-read the LATEST GalleryData right before merging (atomic read-modify-
      // write). A concurrent ensureFullMocksLoaded/ensureAllMocksLoaded may have
      // written between our initial parse and the fetch above; merging into the
      // stale snapshot would clobber its mocks, marking a variant "loaded" while
      // its mocks are missing — which left the surgical cards rendering bare.
      let mergeData;
      try { mergeData = JSON.parse(dataElement.textContent); } catch { mergeData = currentData; }
      if (mergeData.variants && fullMocksData.variants) {
        // Merge mocks for each variant and track which were loaded
        for (const [variantId, variantMocks] of Object.entries(fullMocksData.variants)) {
          if (mergeData.variants[variantId]) {
            // Replace the default mock with the full mocks array
            mergeData.variants[variantId].mocks = variantMocks.mocks;
            // Track this variant as loaded
            this.#loadedVariantIds.add(variantId);
          }
        }

        // Update the GalleryData element with merged data
        dataElement.textContent = JSON.stringify(mergeData);

        return true;
      } else {
        console.warn('[MediaGallery] Unexpected data structure when merging mocks');
        return false;
      }
    } catch (error) {
      console.error('[MediaGallery] Error loading full mocks:', error);
      return false;
    }
  }

  /**
   * Public wrapper for loading full mocks data.
   * Called by sku-picker to ensure mock handles are available for form properties.
   */
  async ensureFullMocksLoaded() {
    return this.#ensureFullMocksLoaded();
  }

  /**
   * Loads full mocks for ALL variants/configurators (e.g. art-print AND canvas-print),
   * not just the current one. Needed by the surgical frame picker, whose cards can span
   * multiple variants (a canvas frame option resolves to a different variant than the
   * current art-print one). Skips variants already loaded.
   */
  async ensureAllMocksLoaded() {
    const dataElement = document.getElementById('GalleryData');
    if (!dataElement) return false;
    let currentData;
    try {
      currentData = JSON.parse(dataElement.textContent);
    } catch {
      return false;
    }
    const allGids = this.#extractConfiguratorGids(currentData, null); // null = all variants
    const unloaded = allGids.filter(g => !this.#loadedVariantIds.has(g.variantId));
    if (!unloaded.length) return true;

    const fullMocksData = await this.#fetchMocksViaGraphQL(unloaded);
    if (!fullMocksData || !fullMocksData.variants) return false;

    // Re-read the LATEST GalleryData right before merging (atomic read-modify-
    // write) so a concurrent loader's mocks aren't clobbered by our stale snapshot.
    let mergeData;
    try { mergeData = JSON.parse(dataElement.textContent); } catch { mergeData = currentData; }
    for (const [variantId, variantMocks] of Object.entries(fullMocksData.variants)) {
      if (mergeData.variants[variantId]) {
        mergeData.variants[variantId].mocks = variantMocks.mocks;
        this.#loadedVariantIds.add(variantId);
      }
    }
    dataElement.textContent = JSON.stringify(mergeData);
    return true;
  }

  /**
   * Returns the product mock data for a given style-picker preset, for use in CSS swatch rendering.
   *
   * Synchronous — caller must have awaited `ensureFullMocksLoaded()` first.
   *
   * Mirrors the option-construction logic in `#findMatchingMocks` exactly:
   * - Canvas products match on frame_color only; regular prints match on size + border + frame_color.
   * - Box-frame color handles are rewritten to classic-frame equivalents.
   * - 'unframed' is appended only when resolvedFraming === 'unframed'.
   *
   * Keep in sync with the SSR mirror in blocks/_product-media-gallery.liquid (the
   * "LCP: re-resolve the default mock" block) — divergence silently regresses PDP LCP.
   *
   * @param {{ resolvedVariantId?: string, resolvedFraming?: string, frameColorHandle?: string, border?: string }} preset
   * @returns {{ frameImage: string, aspectRatio: number, placementTop: number, placementLeft: number, placementWidth: number, transform: string }|null}
   */
  getProductMockForPreset(preset) {
    const variantId = preset.resolvedVariantId || this.#currentVariantId;
    const galleryData = this.#loadGalleryData(variantId);
    if (!galleryData || !galleryData.mocks) return null;

    // Mirror #findMatchingMocks: detect canvas product
    const isCanvasProduct = galleryData.artProduct === 'canvas-print';

    // Mirror #findMatchingMocks: rewrite box-frame -> classic-frame for lookup
    let frameColorForLookup = preset.frameColorHandle || null;
    if (frameColorForLookup && frameColorForLookup.startsWith('box-frame-')) {
      frameColorForLookup = frameColorForLookup.replace(/^box-frame-/, 'classic-frame-');
    }

    let selectedOptions;
    if (isCanvasProduct) {
      // Canvas: match on frame_color only
      selectedOptions = [frameColorForLookup].filter(Boolean);
    } else {
      // Regular art prints: match on size, border, and frame_color
      const size = window.skuPickerInstance?.state?.confirmed?.size_option;
      const border = preset.border || 'no-border';
      selectedOptions = [size, border, frameColorForLookup].filter(Boolean);
    }

    // Mirror #findMatchingMocks: append 'unframed' only when framing is unframed
    if (preset.resolvedFraming === 'unframed') {
      selectedOptions.push('unframed');
    }

    const productMocks = galleryData.mocks.filter(m => m.type === 'product');
    const mock = this.#findBestMatch(productMocks, selectedOptions);

    if (!mock || !mock.frameImage) return null;

    return {
      frameImage: mock.frameImage,
      aspectRatio: mock.aspectRatio,
      placementTop: mock.placementTop,
      placementLeft: mock.placementLeft,
      placementWidth: mock.placementWidth,
      transform: mock.transform,
    };
  }

  /**
   * Extracts configurator GIDs from gallery data.
   *
   * @param {Object} galleryData - Gallery data object
   * @param {string|null} specificVariantId - Optional variant ID to fetch only one variant's mocks
   * @returns {Array<{variantId: string, gid: string}>} - Array of variant ID and configurator GID pairs
   */
  #extractConfiguratorGids(galleryData, specificVariantId = null) {
    const gids = [];

    if (galleryData.variants) {
      // If specific variant requested, only fetch that one
      if (specificVariantId && galleryData.variants[specificVariantId]) {
        const variantData = galleryData.variants[specificVariantId];
        if (variantData.configuratorGid) {
          gids.push({
            variantId: specificVariantId,
            gid: variantData.configuratorGid
          });
        }
      } else {
        // Otherwise fetch all variants (fallback for initial load)
        for (const [variantId, variantData] of Object.entries(galleryData.variants)) {
          if (variantData.configuratorGid) {
            gids.push({
              variantId,
              gid: variantData.configuratorGid
            });
          }
        }
      }
    }

    return gids;
  }

  /**
   * Fetches mock images via GraphQL Storefront API.
   *
   * @param {Array<{variantId: string, gid: string}>} configuratorGids - Configurator GIDs to fetch
   * @returns {Promise<Object|null>} - Mocks data or null if failed
   */
  async #fetchMocksViaGraphQL(configuratorGids) {
    try {
      // Build query to fetch all configurators
      // We'll fetch them one at a time for simplicity, but could batch if needed
      const results = {};

      for (const {variantId, gid} of configuratorGids) {

        // Read the single pre-materialized field (scripts/materialize-configurator-mocks.ts)
        // instead of the old 250-nested product/lifestyle references query — same
        // data, ~instant. The value is already the parsed mock shape.
        const query = `
          query getConfiguratorMocks($id: ID!) {
            metaobject(id: $id) {
              id
              handle
              mocksMaterialized: field(key: "mocks_materialized") { value }
            }
          }
        `;

        // Get Storefront API access token
        const token = this.#getStorefrontAccessToken();
        if (!token) {
          console.error('[MediaGallery] No Storefront API access token found');
          return null;
        }

        const response = await fetch('/api/2025-07/graphql.json', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': token,
          },
          body: JSON.stringify({
            query,
            variables: { id: gid }
          })
        });

        if (!response.ok) {
          console.error(`[MediaGallery] GraphQL request failed for variant ${variantId}:`, response.status);
          continue;
        }

        const result = await response.json();

        if (result.errors) {
          console.error(`[MediaGallery] GraphQL errors for variant ${variantId}:`, result.errors);
          continue;
        }

        if (!result.data?.metaobject) {
          console.error(`[MediaGallery] Metaobject not found for variant ${variantId}: ${gid}`);
          continue;
        }

        // Parse the materialized mocks blob. It's already in parsed shape; re-default
        // the keys the materializer omits (transform/overlayImage/alt/aspectRatio)
        // so downstream sees identical objects to the old #parseMockMetaobject output.
        const raw = result.data.metaobject.mocksMaterialized?.value;
        let variantMocks = { mocks: [] };
        if (raw) {
          try {
            const parsed = JSON.parse(raw);
            variantMocks = {
              mocks: (parsed.mocks || []).map((m) => ({
                handle: m.handle,
                type: m.type,
                frameImage: m.frameImage || null,
                aspectRatio: m.aspectRatio || 'auto',
                placementTop: m.placementTop ?? 0,
                placementLeft: m.placementLeft ?? 0,
                placementWidth: m.placementWidth ?? 100,
                transform: m.transform || 'none',
                overlayImage: m.overlayImage || null,
                alt: m.alt || '',
                options: m.options || [],
              })),
            };
          } catch (e) {
            console.error(`[MediaGallery] Failed to parse mocks_materialized for ${variantId}:`, e);
          }
        }

        results[variantId] = variantMocks;
      }

      // Return in expected format
      return { variants: results };
    } catch (error) {
      console.error('[MediaGallery] Error fetching mocks via GraphQL:', error);
      return null;
    }
  }

  /**
   * Parses a single configurator metaobject from GraphQL into mocks format.
   *
   * @param {Object} configurator - Configurator metaobject from GraphQL
   * @returns {Object} - Parsed mocks data with mocks array
   */
  #parseConfiguratorMetaobject(configurator) {
    // Get mock fields directly from query aliases
    const productMocksField = configurator.productMocks;
    const lifestyleMocksField = configurator.lifestyleMocks;

    const mocks = [];

    // Parse product mocks
    if (productMocksField?.references?.edges) {
      for (const mockEdge of productMocksField.references.edges) {
        const mockObj = mockEdge.node;
        const parsedMock = this.#parseMockMetaobject(mockObj, 'product');
        if (parsedMock) {
          mocks.push(parsedMock);
        }
      }

      // Check for pagination
      if (productMocksField.references.pageInfo?.hasNextPage) {
        console.warn(
          `[MediaGallery] Configurator has more than 250 product mocks. ` +
          `Only first 250 loaded. Consider implementing pagination or reducing mock count.`
        );
      }
    }

    // Parse lifestyle mocks
    if (lifestyleMocksField?.references?.edges) {
      for (const mockEdge of lifestyleMocksField.references.edges) {
        const mockObj = mockEdge.node;
        const parsedMock = this.#parseMockMetaobject(mockObj, 'lifestyle');
        if (parsedMock) {
          mocks.push(parsedMock);
        }
      }

      // Check for pagination
      if (lifestyleMocksField.references.pageInfo?.hasNextPage) {
        console.warn(
          `[MediaGallery] Configurator has more than 250 lifestyle mocks. ` +
          `Only first 250 loaded. Consider implementing pagination or reducing mock count.`
        );
      }
    }

    return { mocks };
  }

  /**
   * Parses a single mock metaobject.
   *
   * @param {Object} mockObj - Mock metaobject from GraphQL
   * @param {string} type - Mock type ('product' or 'lifestyle')
   * @returns {Object|null} - Parsed mock or null
   */
  #parseMockMetaobject(mockObj, type) {
    // Extract options from the references
    const options = mockObj.options?.references?.edges?.map(e => e.node.handle) || [];

    const mock = {
      handle: mockObj.handle,
      type,
      frameImage: mockObj.image?.reference?.image?.url || null,
      aspectRatio: mockObj.aspectRatio?.value || 'auto',
      placementTop: parseFloat(mockObj.placementTop?.value || 0),
      placementLeft: parseFloat(mockObj.placementLeft?.value || 0),
      placementWidth: parseFloat(mockObj.placementWidth?.value || 100),
      transform: mockObj.transform?.value || 'none',
      overlayImage: mockObj.overlayImage?.reference?.image?.url || null,
      alt: mockObj.altText?.value || '',
      options
    };

    return mock;
  }

  /**
   * Gets the Storefront API access token.
   *
   * @returns {string|null} - Access token or null if not found.
   */
  #getStorefrontAccessToken() {
    // Check for token in meta tag (most common in modern themes)
    const metaTag = document.querySelector('meta[name="shopify-storefront-api-token"]');
    if (metaTag?.content) {
      return metaTag.content;
    }

    // Check for token in data attribute
    const tokenElement = document.querySelector('[data-storefront-access-token]');
    if (tokenElement?.dataset.storefrontAccessToken) {
      return tokenElement.dataset.storefrontAccessToken;
    }

    // Check in window.Shopify global
    if (window.Shopify?.storefrontAccessToken) {
      return window.Shopify.storefrontAccessToken;
    }

    return null;
  }

  /**
   * Gets the product handle from the current page.
   *
   * @returns {string|null} - Product handle or null if not found.
   */
  #getProductHandle() {
    // Try to get from form action
    const productForm = document.querySelector('form[action*="/cart/add"]');
    if (productForm) {
      const action = productForm.getAttribute('action');
      const match = action.match(/\/products\/([^?]+)/);
      if (match) {
        return match[1];
      }
    }

    // Try to get from URL
    const urlMatch = window.location.pathname.match(/\/products\/([^/?]+)/);
    if (urlMatch) {
      return urlMatch[1];
    }

    return null;
  }

  /**
   * Finds mocks that match the selected options.
   *
   * @param {Array} galleryData - The gallery data array.
   * @param {Object} selection - The current selection (size_option, framing_option, border_option, frame_color).
   * @returns {Object} - Object with productMock and lifestyleMocks arrays.
   */
  #findMatchingMocks(galleryData, selection) {
    // Detect if this is a canvas product (no border options, size agnostic)
    // Canvas products only match on frame_color
    const isCanvasProduct = galleryData.artProduct === 'canvas-print';

    // Replace box-frame with classic-frame in frame_color for mock lookups
    // Box frames reuse classic frame mocks, so frame colors are tagged with classic-frame handles
    let frameColorForLookup = selection.frame_color;
    if (frameColorForLookup && frameColorForLookup.startsWith('box-frame-')) {
      frameColorForLookup = frameColorForLookup.replace(/^box-frame-/, 'classic-frame-');
    }

    let selectedOptions;

    if (isCanvasProduct) {
      // Canvas products: only match on frame_color
      selectedOptions = [
        frameColorForLookup
      ].filter(Boolean);

      // Only include framing_option when it's "unframed"
      // Framed products share mocks across different frame types (oak, walnut, etc.)
      // and are differentiated by frame_color instead
      if (selection.framing_option === 'unframed') {
        selectedOptions.push('unframed');
      }
    } else {
      // Regular art prints: match on size, border, and frame_color
      selectedOptions = [
        selection.size_option,
        selection.border_option,
        frameColorForLookup
      ].filter(Boolean);

      // Only include framing_option when it's "unframed"
      // Framed products share mocks across different frame types (oak, walnut, etc.)
      // and are differentiated by frame_color instead
      if (selection.framing_option === 'unframed') {
        selectedOptions.push('unframed');
      }
    }


    // Find the best matching product mock
    const productMocks = galleryData.mocks.filter(item => item.type === 'product');

    const productMock = this.#findBestMatch(productMocks, selectedOptions);

    // Find all matching lifestyle mocks
    const lifestyleMocks = galleryData.mocks.filter(item => item.type === 'lifestyle');

    const matchingLifestyleMocks = lifestyleMocks.filter(mock =>
      this.#mockMatchesSelection(mock, selectedOptions)
    );

    return {
      productMock: productMock,
      lifestyleMocks: matchingLifestyleMocks
    };
  }

  /**
   * True when two image URLs reference the same underlying CDN file,
   * ignoring host (www.iamfy.co/cdn/shop vs cdn.shopify.com/s/files) and
   * query params (width/quality/v). Shopify file names are unique per file.
   *
   * @param {string} a
   * @param {string} b
   * @returns {boolean}
   */
  static #sameMockFile(a, b) {
    const fileOf = (u) => (u || '').split('?')[0].split('/').pop();
    const fa = fileOf(a);
    const fb = fileOf(b);
    return !!fa && fa === fb;
  }

  /**
   * Finds the best matching mock from an array of mocks.
   * Prioritizes mocks with more matching options.
   *
   * Keep in sync with the SSR mirror in blocks/_product-media-gallery.liquid (the
   * "LCP: re-resolve the default mock" block) — divergence silently regresses PDP LCP.
   *
   * @param {Array} mocks - Array of mock objects.
   * @param {Array} selectedOptions - Array of selected option handles.
   * @returns {Object|null} - Best matching mock or null.
   */
  #findBestMatch(mocks, selectedOptions) {
    let bestMatch = null;
    let bestScore = 0;

    for (const mock of mocks) {
      const score = this.#calculateMatchScore(mock, selectedOptions);
      if (score > bestScore) {
        bestScore = score;
        bestMatch = mock;
      }
    }

    return bestMatch;
  }

  /**
   * Calculates how many selected options match a mock's options.
   *
   * @param {Object} mock - Mock object with options array.
   * @param {Array} selectedOptions - Array of selected option handles.
   * @returns {number} - Number of matching options.
   */
  #calculateMatchScore(mock, selectedOptions) {
    if (!mock.options || !Array.isArray(mock.options)) return 0;

    return selectedOptions.filter(option =>
      mock.options.includes(option)
    ).length;
  }

  /**
   * Checks if a mock matches all selected options.
   *
   * @param {Object} mock - Mock object with options array.
   * @param {Array} selectedOptions - Array of selected option handles.
   * @returns {boolean} - True if all selected options are in mock's options.
   */
  #mockMatchesSelection(mock, selectedOptions) {
    if (!mock.options || !Array.isArray(mock.options)) return false;

    return selectedOptions.every(option => mock.options.includes(option));
  }

  /**
   * Updates the gallery DOM with new mocks.
   *
   * @param {Object} matchingMocks - Object with productMock and lifestyleMocks.
   */
  async #updateGalleryMocks(matchingMocks) {
    // Allow subclasses to skip mock updates (e.g., AI Photos preserves Liquid-rendered content)
    if (!this._shouldUpdateGalleryMocks()) {
      return;
    }

    const { productMock, lifestyleMocks } = matchingMocks;

    // Batch updates with requestAnimationFrame breaks to avoid blocking
    // Update carousel/slideshow if present
    if (this.slideshow) {
      await new Promise(resolve => requestAnimationFrame(resolve));
      await this.#updateCarouselMocks(productMock, lifestyleMocks);
    }

    // Update grid if present
    const grid = this.querySelector('.media-gallery__grid');
    if (grid) {
      await new Promise(resolve => requestAnimationFrame(resolve));
      await this.#updateGridMocks(grid, productMock, lifestyleMocks);
    }

    // Update zoom dialog if present
    if (this.refs.zoomDialogComponent) {
      await new Promise(resolve => requestAnimationFrame(resolve));
      await this.#updateZoomDialogMocks(productMock, lifestyleMocks);
    }

    // Re-apply uploaded photo if one exists (subclass hook)
    // This ensures the uploaded photo is shown even after mock images are updated
    if (this._getUploadedPhotoUrl()) {
      await this._updateGalleryWithUploadedPhoto();
    }
  }

  /**
   * Updates carousel/slideshow mock images.
   *
   * @param {Object|null} productMock - The matching product mock.
   * @param {Array} lifestyleMocks - Array of matching lifestyle mocks.
   */
  async #updateCarouselMocks(productMock, lifestyleMocks) {

    // Use slideshow-slide elements (with ref="slides[]")
    const slides = this.slideshow.querySelectorAll('slideshow-slide[ref="slides[]"]');

    if (!slides || slides.length === 0) {
      console.warn('[MediaGallery] No slides found in carousel');
      return;
    }

    // Warm every frame image in parallel before the slide loop —
    // #updateMediaElement awaits its mock's preload, so without this the
    // slides update strictly serially and a multi-mock product stalls the
    // gallery for the sum of all image fetches instead of the slowest one.
    const allMocks = [productMock, ...lifestyleMocks].filter((m) => m?.frameImage);
    await Promise.all(allMocks.map((m) => this.#preloadImage(m.frameImage)));

    let mockIndex = 0;

    for (let index = 0; index < slides.length; index++) {
      const slide = slides[index];

      // In slideshow, .product-media is a direct child of the slide (no container wrapper)
      const productMedia = slide.querySelector('.product-media');

      if (!productMedia) {
        continue;
      }

      const mediaId = productMedia.dataset.mediaId;

      // Check if this is a mock (first slide is product mock, subsequent are
      // lifestyle mocks). On the new PDP slide 0 is a frame-mock managed by
      // #swapHeroMock, but its snippets (mock-image / single-print-frame-mock)
      // render no .product-media element, so the continue above skips it —
      // this index-0 path only runs on layouts where slide 0 IS a
      // product-media mock slide.
      if (index === 0 && productMock) {
        // Update product mock (first slide)
        await this.#updateMediaElement(productMedia, productMock);
        this.#updateCarouselThumbnail(index, productMock);
      } else if (index > 0 && mediaId && this.#isMockMediaId(mediaId)) {
        // Static slides are variant-scoped generic images that refresh
        // server-side; skip them WITHOUT consuming a lifestyle-mock slot so the
        // real lifestyle mocks keep their positional mapping.
        if (this.#isStaticSlide(productMedia)) {
          continue;
        }
        // This is a lifestyle mock slide
        const lifestyleMock = lifestyleMocks[mockIndex];
        if (lifestyleMock) {
          await this.#updateMediaElement(productMedia, lifestyleMock);
          this.#updateCarouselThumbnail(index, lifestyleMock);
          this.#updateConfigButton(slide, lifestyleMock);
          mockIndex++;
        }
      }
    }

  }

  /**
   * Updates a "Choose This Configuration" button's data-mock-options to reflect
   * the new lifestyle mock shown on a slide. Maps flat option handles from the
   * mock to typed objects using the sku-picker's option data.
   *
   * @param {HTMLElement} slide - The slideshow-slide element containing the button.
   * @param {Object} mock - The lifestyle mock object with an options array of handles.
   */
  #updateConfigButton(slide, mock) {
    const btn = slide.querySelector('.choose-config-btn');
    if (!btn || !mock.options || !Array.isArray(mock.options)) return;

    const sp = window.skuPickerInstance;
    if (!sp || !sp.data?.options) return;

    // Build a handle→type lookup from sku-picker option data
    const handleTypeMap = {};
    const optionTypes = { size: 'size', framing: 'framing', border: 'border' };
    for (const [type, key] of Object.entries(optionTypes)) {
      for (const opt of (sp.data.options[key] || [])) {
        handleTypeMap[opt.handle] = { type: type === 'border' ? 'border' : type, name: opt.name || opt.handle };
        // Also map frame colors
        if (opt.colors) {
          for (const color of opt.colors) {
            handleTypeMap[color.handle] = { type: 'frame_color', name: color.name || color.handle };
          }
        }
      }
    }

    // Map mock option handles to typed objects for the button
    const typedOptions = [];
    const seenTypes = new Set();
    for (const handle of mock.options) {
      const info = handleTypeMap[handle];
      if (info && !seenTypes.has(info.type)) {
        seenTypes.add(info.type);
        typedOptions.push({ type: info.type, name: info.name, handle });
      }
    }

    if (typedOptions.length > 0) {
      btn.dataset.mockOptions = JSON.stringify(typedOptions);
    }
  }

  /**
   * Updates a carousel thumbnail image.
   *
   * @param {number} index - The slide index.
   * @param {Object} mock - The mock object with frame image.
   */
  #updateCarouselThumbnail(index, mock) {

    if (!mock || !mock.frameImage) {
      console.warn(`[MediaGallery] No mock or frameImage for thumbnail ${index}`);
      return;
    }

    // Update the thumbnail in EVERY strip — the desktop strip carries
    // ref="slideshowControls" but the mobile strip renders with
    // secondary: true and no ref (see slideshow-controls.liquid), so a
    // ref-scoped query left mobile thumbnails showing the old framing.
    // Both strips render the same thumbnails[] list, so indexes align
    // (mirrors #patchAdditionalThumbnails).
    const allControls = this.querySelectorAll('slideshow-controls');
    if (allControls.length === 0) {
      console.warn('[MediaGallery] No slideshow controls found');
      return;
    }
    for (const slideshowControls of allControls) {
      this.#updateThumbnailInStrip(slideshowControls, index, mock);
    }
  }

  /**
   * Updates the thumbnail at `index` inside one slideshow-controls strip.
   *
   * @param {HTMLElement} slideshowControls - The strip to update.
   * @param {number} index - The slide index.
   * @param {Object} mock - The mock object with frame image.
   */
  #updateThumbnailInStrip(slideshowControls, index, mock) {
    const thumbnails = slideshowControls.querySelectorAll('button[ref="thumbnails[]"]');

    if (!thumbnails || thumbnails.length === 0) {
      return;
    }

    const thumbnail = thumbnails[index];
    if (!thumbnail) {
      return;
    }

    // CSS-mode frame-mock thumbnails are kept in sync with the hero by
    // #swapHeroMock, which sets BOTH the frame image and the artwork placement.
    // This path only sets the frame image, so letting it run here would leave
    // the thumbnail's placement stale (mismatching the hero). Skip it. Canvas /
    // gallery-wall frame thumbnails (no .mock-image__frame-container) are not
    // touched by #swapHeroMock, so they still update here.
    if (thumbnail.dataset.slideRole === 'frame-mock' && thumbnail.querySelector('.mock-image__frame-container')) {
      return;
    }

    // Update artwork if uploaded photo exists
    const mockContainer = thumbnail.querySelector('.mock-image-container');
    const uploadedPhotoUrl = this._getUploadedPhotoUrl();
    if (mockContainer && uploadedPhotoUrl) {
      mockContainer.dataset.artworkSrc = uploadedPhotoUrl;
      mockContainer.dispatchEvent(new CustomEvent('artwork-changed', { bubbles: false }));
    }

    // Thumbnails use mock-image snippet which has different structures based on render mode:
    // - CSS mode: .mock-image__frame-container (div with background-image) - THIS IS THE VISIBLE FRAME
    // - CSS3D/Canvas mode: .mock-image__frame (div with background-image)
    // We need to update the visible frame, not the artwork or the frame-image img

    // Try CSS mode: .mock-image__frame-container (div with background-image)
    // This is the visible frame layer in CSS mode thumbnails
    const frameContainer = thumbnail.querySelector('.mock-image__frame-container');

    if (frameContainer) {
      frameContainer.style.backgroundImage = `url(${mock.frameImage})`;
      return;
    }

    // Try CSS3D/Canvas mode: .mock-image__frame (div with background-image)
    const frameDiv = thumbnail.querySelector('.mock-image__frame');

    if (frameDiv) {
      frameDiv.style.backgroundImage = `url(${mock.frameImage})`;
      return;
    }

    // Fallback: if no mock structure found, try regular img (non-mock thumbnail)
    const img = thumbnail.querySelector('img');
    if (img) {
      img.src = mock.frameImage;
      if (mock.alt) {
        img.alt = mock.alt;
      }
    } else {
      console.warn(`[MediaGallery] No img or frame element found in thumbnail ${index}`);
    }
  }

  /**
   * Updates room mock thumbnails when the main slide's room background loads.
   * The thumbnail initially renders with a plain-wall fallback; this passes
   * the real room data so it re-renders with correct background and frame position.
   */
  #updateRoomThumbnailBackground(roomBg, source) {
    if (!roomBg?.imageUrl) return;
    // Room thumbnails are now CSS-background placeholders ([data-room-thumb-photo]),
    // not <room-background-mock> canvases. Paint the resolved room photo onto them
    // (desktop + mobile slideshow controls) when the main slide's room changes.
    const url = source?.getOptimizedImageUrl
      ? source.getOptimizedImageUrl(roomBg.imageUrl, 600)
      : roomBg.imageUrl;
    const css = `url("${url}")`;
    const thumbs = this.querySelectorAll('[data-room-thumb-photo]');
    for (const thumb of thumbs) {
      thumb.style.backgroundImage = css;
      thumb.style.backgroundSize = 'cover';
      thumb.style.backgroundPosition = 'center';
    }
  }

  /**
   * Checks if a media ID represents a mock (not a numeric Shopify media ID).
   *
   * @param {string} mediaId - The media ID to check.
   * @returns {boolean} - True if this is a mock media ID.
   */
  #isMockMediaId(mediaId) {
    // Mock IDs are handles (strings), Shopify media IDs are numeric
    return isNaN(parseInt(mediaId, 10));
  }

  /**
   * Checks whether a .product-media element is a static (render_mode: 'static')
   * slide — a generic no-artwork image that refreshes server-side on variant
   * change. The variant-swap loops must skip these WITHOUT consuming a
   * lifestyle-mock slot, so the real mocks keep their positional mapping.
   *
   * Single source of truth for the `.mock-image-container--static` DOM hook so
   * the four swap sites (carousel, grid, zoom thumbnails, #updateMediaElement
   * backstop) can never drift apart if the class name changes.
   *
   * @param {HTMLElement} productMedia - A .product-media element (or its container).
   * @returns {boolean} - True if this is a static slide.
   */
  #isStaticSlide(productMedia) {
    return !!productMedia?.querySelector('.mock-image-container--static');
  }

  /**
   * Updates a single product-media element with new mock data.
   * Recreates the complete layered mock image structure with preloading and fade transition.
   *
   * @param {HTMLElement} mediaElement - The .product-media element.
   * @param {Object} mock - The mock object with complete metaobject data.
   */
  async #updateMediaElement(mediaElement, mock) {

    if (!mediaElement || !mock) {
      console.warn('[MediaGallery] updateMediaElement: Missing mediaElement or mock');
      return;
    }

    // Static slides (render_mode: 'static') show a fixed image with no artwork
    // composited in. They're variant-scoped and refresh server-side on variant
    // change (#refreshAdditionalSlides), so the client-side selection swap must
    // never inject artwork into them. The per-loop skip below normally means we
    // never reach here for a static slide; this is a defensive backstop.
    if (this.#isStaticSlide(mediaElement)) {
      return;
    }

    // Preload the new frame image before updating to prevent flash.
    // Skip when the existing frame <img> already points at the same underlying
    // CDN file (e.g. SSR renders width=800&quality=60, mock stores width=1600):
    // firing a preload would trigger a second network request for the same
    // image and introduce a late LCP candidate.
    if (mock.frameImage) {
      const existingFrameImg = mediaElement.querySelector('.mock-image__frame-bg');
      const existingSrc = existingFrameImg ? existingFrameImg.src : null;
      if (!MediaGallery.#sameMockFile(existingSrc, mock.frameImage)) {
        await this.#preloadImage(mock.frameImage);
      }
    }

    // Update data-media-id
    const oldMediaId = mediaElement.dataset.mediaId;
    mediaElement.dataset.mediaId = mock.handle;

    // Check for CSS3D mode first (doesn't use mock-image-container wrapper)
    const css3dElement = mediaElement.querySelector('mock-image-css3d');
    if (css3dElement) {
      // CSS3D mode: Update frame src and overlay
      if (mock.frameImage) {
        css3dElement.dataset.frameSrc = mock.frameImage;

        // Update the lighting layer's frame image
        const lightingLayer = css3dElement.querySelector('.mock-image__frame-lighting');
        if (lightingLayer) {
          lightingLayer.src = mock.frameImage;
        }
      }

      // Update overlay image if present
      const overlayImg = css3dElement.querySelector('.mock-image__overlay');
      if (mock.overlayImage) {
        if (overlayImg) {
          overlayImg.src = mock.overlayImage;
        }
      } else if (overlayImg) {
        // Remove overlay if mock doesn't have one
        overlayImg.remove();
      }

      return; // Don't rebuild the entire structure for CSS3D mode
    }

    // Find the mock-image-container or create the structure
    let mockContainer = mediaElement.querySelector('.mock-image-container');
    const isCanvasMode = mockContainer && mockContainer.classList.contains('mock-image-container--canvas');

    // Update artwork data attribute if uploaded photo exists (before mode-specific handling)
    const uploadedPhotoUrl = this._getUploadedPhotoUrl();
    if (mockContainer && uploadedPhotoUrl) {
      mockContainer.dataset.artworkSrc = uploadedPhotoUrl;
      // Dispatch event to notify CSS3D/canvas components to update
      mockContainer.dispatchEvent(new CustomEvent('artwork-changed', { bubbles: false }));
    }

    // For canvas mode mocks, update the frame image on the mock-image-canvas element
    if (isCanvasMode) {
      const canvasElement = mockContainer.querySelector('mock-image-canvas');
      if (canvasElement && mock.frameImage) {
        // Update the frame image data attribute
        canvasElement.dataset.frameSrc = mock.frameImage;

        // Trigger re-render by calling loadAndRender if available
        if (typeof canvasElement.loadAndRender === 'function') {
          await canvasElement.loadAndRender();
        }
      }
      return; // Don't rebuild the entire structure for canvas mode
    }

    if (!mockContainer) {
      // If no mock container exists, we need to create the entire mock structure
      mockContainer = document.createElement('div');
      mockContainer.className = 'mock-image-container';

      // Clear existing content and append new container
      const existingImage = mediaElement.querySelector('.product-media__image');
      if (existingImage) {
        existingImage.replaceWith(mockContainer);
      } else {
        mediaElement.appendChild(mockContainer);
      }
    }

    // Set aspect ratio
    if (mock.aspectRatio && mock.aspectRatio !== 'auto') {
      mockContainer.style.aspectRatio = mock.aspectRatio;
    }

    // Build the placement style for artwork and overlay (matches mock-image.liquid)
    const placementStyle = `top: ${mock.placementTop || 0}%; left: ${mock.placementLeft || 0}%; width: ${mock.placementWidth || 100}%; transform: ${mock.transform || 'none'};`;

    // Check if this is CSS mode with frame-container structure
    const isCssMode = mockContainer.classList.contains('mock-image-container--css');
    let frameContainer = mockContainer.querySelector('.mock-image__frame-container');

    if (isCssMode && mock.frameImage) {
      // CSS mode: Update frame (either as img or background-image)
      if (!frameContainer) {
        frameContainer = document.createElement('div');
        frameContainer.className = 'mock-image__frame-container';
        mockContainer.appendChild(frameContainer);
      }

      // Check if frame is rendered as <img> (main product media uses this for LCP)
      let frameBgImg = frameContainer.querySelector('.mock-image__frame-bg');

      if (frameBgImg) {
        if (!MediaGallery.#sameMockFile(frameBgImg.src, mock.frameImage)) {
          // Safari fix: Remove srcset before updating src to prevent caching issues
          frameBgImg.removeAttribute('srcset');
          // Force a reflow to ensure Safari processes the attribute removal
          void frameBgImg.offsetHeight;
          frameBgImg.src = mock.frameImage;
        }
      } else {
        // No <img> element, use background-image instead (non-main media)
        frameContainer.style.backgroundImage = `url(${mock.frameImage})`;
      }

      // Get or create placement container inside frame-container
      let placementContainer = frameContainer.querySelector('.mock-image__placement');
      if (!placementContainer) {
        placementContainer = document.createElement('div');
        placementContainer.className = 'mock-image__placement';
        placementContainer.style.cssText = placementStyle;
        frameContainer.appendChild(placementContainer);
      } else {
        placementContainer.style.cssText = placementStyle;
      }

      // Get existing images within placement container
      let artworkImg = placementContainer.querySelector('.mock-image__artwork');
      let overlayImg = placementContainer.querySelector('.mock-image__overlay');

      // Update or create artwork image
      if (this.#mainArtwork) {
        // Use uploaded photo if available, otherwise use main artwork
        const artworkSrc = uploadedPhotoUrl || this.#mainArtwork;

        if (!artworkImg) {
          artworkImg = document.createElement('img');
          artworkImg.className = 'mock-image__artwork';
          artworkImg.src = artworkSrc;
          artworkImg.alt = this.#mainArtworkAlt || mock.alt || '';
          artworkImg.loading = 'lazy';
          placementContainer.appendChild(artworkImg);
        } else {
          // Update existing artwork src (e.g., when user uploads a photo)
          artworkImg.src = artworkSrc;
          // Remove srcset so browser uses our updated src instead
          artworkImg.removeAttribute('srcset');
          artworkImg.removeAttribute('sizes');
        }
      }

      // Update or create overlay image
      if (mock.overlayImage) {
        if (!overlayImg) {
          overlayImg = document.createElement('img');
          overlayImg.className = 'mock-image__overlay';
          overlayImg.alt = '';
          overlayImg.setAttribute('aria-hidden', 'true');
          overlayImg.loading = 'lazy';
          placementContainer.appendChild(overlayImg);
        }
        overlayImg.src = mock.overlayImage;
      } else if (overlayImg) {
        overlayImg.remove();
      }
    } else {
      // Non-CSS mode (simple structure): Direct children of mockContainer
      let artworkImg = mockContainer.querySelector('.mock-image__artwork');
      let overlayImg = mockContainer.querySelector('.mock-image__overlay');
      let frameImg = mockContainer.querySelector('.mock-image__frame');

      // Update or create artwork image (using main artwork from product)
      if (this.#mainArtwork) {
        // Use uploaded photo if available, otherwise use main artwork
        const artworkSrc = uploadedPhotoUrl || this.#mainArtwork;

        if (!artworkImg) {
          artworkImg = document.createElement('img');
          artworkImg.className = 'mock-image__artwork';
          artworkImg.src = artworkSrc;
          artworkImg.alt = this.#mainArtworkAlt || mock.alt || '';
          artworkImg.loading = 'lazy';
          artworkImg.style.cssText = placementStyle;
          mockContainer.appendChild(artworkImg);
        } else {
          // Update existing artwork src and placement
          artworkImg.src = artworkSrc;
          artworkImg.style.cssText = placementStyle;
          // Remove srcset so browser uses our updated src instead
          artworkImg.removeAttribute('srcset');
          artworkImg.removeAttribute('sizes');
        }
      }

      // Update or create overlay image if present
      if (mock.overlayImage) {
        if (!overlayImg) {
          overlayImg = document.createElement('img');
          overlayImg.className = 'mock-image__overlay';
          overlayImg.alt = '';
          overlayImg.setAttribute('aria-hidden', 'true');
          overlayImg.loading = 'lazy';
          mockContainer.appendChild(overlayImg);
        }
        // Update the overlay image source and placement
        overlayImg.src = mock.overlayImage;
        overlayImg.style.cssText = placementStyle;
      } else if (overlayImg) {
        // Remove overlay if it exists but mock doesn't have one
        overlayImg.remove();
      }

      // Update or create frame image
      if (mock.frameImage) {
        if (!frameImg) {
          frameImg = document.createElement('img');
          frameImg.className = 'mock-image__frame';
          frameImg.alt = '';
          frameImg.setAttribute('aria-hidden', 'true');
          frameImg.loading = 'lazy';
          mockContainer.appendChild(frameImg);
        }
        // Update the frame image source (this is what changes between mocks)
        frameImg.src = mock.frameImage;
      } else if (frameImg) {
        // Remove frame if it exists but mock doesn't have one
        frameImg.remove();
      }
    }
  }

  /**
   * Preloads an image to ensure smooth transitions.
   *
   * @param {string} src - Image URL to preload.
   * @returns {Promise} - Resolves when image is loaded.
   */
  #preloadImage(src) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve();
      img.onerror = () => {
        console.warn('[MediaGallery] Failed to preload image:', src);
        resolve(); // Resolve anyway to not block the update
      };
      img.src = src;
    });
  }

  /**
   * Updates grid mock images.
   *
   * @param {HTMLElement} grid - The grid container element.
   * @param {Object|null} productMock - The matching product mock.
   * @param {Array} lifestyleMocks - Array of matching lifestyle mocks.
   */
  async #updateGridMocks(grid, productMock, lifestyleMocks) {

    const gridItems = grid.querySelectorAll('li[ref="media[]"]');
    if (!gridItems || gridItems.length === 0) return;

    let mockIndex = 0;

    for (let index = 0; index < gridItems.length; index++) {
      const item = gridItems[index];
      const productMedia = item.querySelector('.product-media');
      if (!productMedia) continue;

      const mediaId = productMedia.dataset.mediaId;

      // Check if this is a mock
      if (index === 0 && productMock) {
        // Update product mock (first grid item)
        await this.#updateMediaElement(productMedia, productMock);
      } else if (index > 0 && mediaId && this.#isMockMediaId(mediaId)) {
        // Static slides refresh server-side; skip without consuming a slot.
        if (this.#isStaticSlide(productMedia)) {
          continue;
        }
        // This is a lifestyle mock grid item
        const lifestyleMock = lifestyleMocks[mockIndex];
        if (lifestyleMock) {
          await this.#updateMediaElement(productMedia, lifestyleMock);
          this.#updateConfigButton(item, lifestyleMock);
          mockIndex++;
        }
      }
    }
  }

  /**
   * Updates zoom dialog mock images.
   * NOTE: Zoom gallery shows artwork-only (force_no_mock: true), so we only update thumbnails.
   *
   * @param {Object|null} productMock - The matching product mock.
   * @param {Array} lifestyleMocks - Array of matching lifestyle mocks.
   */
  async #updateZoomDialogMocks(productMock, lifestyleMocks) {
    const zoomDialog = this.refs.zoomDialogComponent;
    if (!zoomDialog) return;

    // DON'T update the zoomed gallery items - they show artwork-only and never change
    // The zoom gallery is rendered with force_no_mock: true and always displays main_artwork_media

    // Update the thumbnail list in zoom dialog
    const thumbnailList = zoomDialog.querySelector('.dialog-thumbnails-list');
    if (thumbnailList) {
      const thumbnails = thumbnailList.querySelectorAll('.dialog-thumbnails-list__thumbnail');
      this.#updateZoomThumbnails(thumbnails, productMock, lifestyleMocks);
    }
  }

  /**
   * Updates zoom dialog thumbnail images.
   *
   * @param {NodeList} thumbnails - The thumbnail button elements.
   * @param {Object|null} productMock - The matching product mock.
   * @param {Array} lifestyleMocks - Array of matching lifestyle mocks.
   */
  #updateZoomThumbnails(thumbnails, productMock, lifestyleMocks) {
    if (!thumbnails || thumbnails.length === 0) return;

    // NOTE (by design): on the new carousel PDP this loop is a no-op —
    // refs.media only populates from the grid layout's ref="media[]"
    // elements, so every iteration bails at the mediaElement check. That is
    // correct: the zoom dialog renders artwork-only thumbnails (the Liquid
    // dedupes all MOCK| entries to a single main_artwork_media thumbnail
    // and skips FRAME|/ROOM|), so framing changes have nothing to update
    // there. Only the legacy grid layout reaches the per-index updates.

    let mockIndex = 0;

    thumbnails.forEach((thumbnail, index) => {
      const img = thumbnail.querySelector('img');
      if (!img) return;

      // Get the corresponding media element to check if it's a mock
      const mediaElement = this.refs.media?.[index];
      if (!mediaElement) return;

      const productMedia = mediaElement.querySelector('.product-media');
      if (!productMedia) return;

      const mediaId = productMedia.dataset.mediaId;

      // Check if this is a mock
      if (index === 0 && productMock && productMock.frameImage) {
        // Update product mock thumbnail
        img.src = productMock.frameImage;
        if (productMock.alt) img.alt = productMock.alt;
      } else if (index > 0 && mediaId && this.#isMockMediaId(mediaId)) {
        // Static slides refresh server-side; skip without consuming a slot.
        if (this.#isStaticSlide(productMedia)) {
          return;
        }
        // This is a lifestyle mock thumbnail
        const lifestyleMock = lifestyleMocks[mockIndex];
        if (lifestyleMock && lifestyleMock.frameImage) {
          img.src = lifestyleMock.frameImage;
          if (lifestyleMock.alt) img.alt = lifestyleMock.alt;
          mockIndex++;
        }
      }
    });
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
